import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { PayerDetails } from '@/services/scheduling/use-prebook-selected-patient';

/**
 * GET /api/Billing/Agreements/InsurancePayerDetails
 *   ?organizationId=&showArchived=&q=
 *
 * Mirrors Flutter `searchInsurance()` in `common_data_source_impl.dart:573–580`
 * — typeahead for the Insurance editor's payer field. Disabled until the
 * user types at least 2 chars (matches the Flutter behaviour where the
 * dropdown only fires after a query is set).
 */

interface UseSearchInsurancePayersParams {
  organizationId: string;
  query: string;
  showArchived?: boolean;
}

export function useSearchInsurancePayers({
  organizationId,
  query,
  showArchived = false,
}: UseSearchInsurancePayersParams) {
  const trimmed = query.trim();
  const enabled = !!organizationId && trimmed.length >= 2;

  return useQuery<PayerDetails[]>({
    queryKey: ['insurance-payers', { organizationId, query: trimmed, showArchived }],
    enabled,
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({
        organizationId,
        showArchived: String(showArchived),
        q: trimmed,
      });
      // The backend returns 204 (No Content) when there are no matches —
      // `parseResponse` translates an empty body to `undefined`, which
      // React Query v5 treats as an error. Coerce to `[]` here so the
      // dropdown shows "No matches" instead of triggering the global
      // error toast.
      const result = await get<PayerDetails[] | undefined>(
        `/api/Billing/Agreements/InsurancePayerDetails?${params.toString()}`,
        { signal },
      );
      return result ?? [];
    },
  });
}
